import React, { useMemo, useState } from "react";
import AccordionSection, { AccordionSectionHandle } from "../../utils/accordion/AccordionSection";
import { renderSafeHtml } from "../../utils/RenderTemplate";
import "./styles/PropertyDetailsSection.css";

export type DetailsPanelSpec = {
  label: string;
  content: string;
  source?: "parcelsLayer" | "layersLayer";
  layerNumber?: number;
};

export type DetailsTabSpec = {
  header: string;
  panels: DetailsPanelSpec[];
};

type Props = {
  layout: DetailsTabSpec[];
  attrs?: Record<string, any>;
  layerAttrs?: Record<number, any[]>;
  defaultOpen?: boolean;
  sectionRef?: React.Ref<AccordionSectionHandle>;
};

const EMPTY_RE = /^(?:\s*|null|undefined|N\/A)$/i;

const PropertyDetailsSection: React.FC<Props> = ({
  layout,
  attrs = {},
  layerAttrs = {},
  defaultOpen,
  sectionRef
}) => {
  const [active, setActive] = useState(0);
  const tabs = useMemo(() => layout ?? [], [layout]);
  const multi = tabs.length > 1;

  const getAttrsForPanel = (p: DetailsPanelSpec): Record<string, any> => {
    if (p.source === "layersLayer" && typeof p.layerNumber === "number") {
      const arr = layerAttrs[p.layerNumber];
      if (Array.isArray(arr) && arr.length) return arr[0] ?? {};
      return {};
    }
    return attrs ?? {};
  };

  const isPanelEmpty = (p: DetailsPanelSpec) => {
    const rowAttrs = getAttrsForPanel(p);
    const html = renderSafeHtml(p.content ?? "", rowAttrs);
    const text = html.replace(/<[^>]*>/g, "").trim();
    return EMPTY_RE.test(text);
  };

  const hasAnyValue = useMemo(() => {
    for (const t of tabs) {
      for (const p of (t.panels || [])) {
        if (!isPanelEmpty(p)) return true;
      }
    }
    return false;
  }, [tabs, attrs, layerAttrs]);

  const renderRow = (p: DetailsPanelSpec) => {
    const rowAttrs = getAttrsForPanel(p);
    const html = renderSafeHtml(p.content ?? "", rowAttrs);
    const textOnly = html.replace(/<[^>]*>/g, "").trim();
    const empty = EMPTY_RE.test(textOnly);

    return (
      <div className="pd-row" key={p.label}>
        <div className="pd-label">{p.label}</div>
        <div className="pd-value">
          {empty ? (
            <span className="pd-empty-value">No value available</span>
          ) : (
            <span dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
      </div>
    );
  };

  return (
    <AccordionSection ref={sectionRef} title="Property Details" defaultOpen={defaultOpen}>
      {!tabs.length || !hasAnyValue ? (
        <div className="pd-empty">
          Use the search tools (above), or the map (right) to find and select a property.
          Once selected, property details will appear here.
        </div>
      ) : (
        <div className="pd">
          {multi && (
            <div className="pd-tabs" role="tablist" aria-label="Property detail sections">
              {tabs.map((t, i) => (
                <button
                  key={t.header + i}
                  role="tab"
                  aria-selected={i === active}
                  className={`pd-tab ${i === active ? "is-active" : ""}`}
                  onClick={() => setActive(i)}
                >
                  {t.header}
                </button>
              ))}
            </div>
          )}
          <div className="pd-panels">
            {(tabs[multi ? active : 0].panels || []).map(renderRow)}
          </div>
        </div>
      )}
    </AccordionSection>
  );
};

export default PropertyDetailsSection;
